import { NgClass } from "@angular/common";
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from "@angular/core";
import { NgbAccordionBody, NgbAccordionButton, NgbAccordionCollapse, NgbAccordionDirective, NgbAccordionHeader, NgbAccordionItem, NgbAccordionToggle, NgbCollapse } from "@ng-bootstrap/ng-bootstrap";
import { isEqual, some } from "lodash";
import { ICharacteristicTemplate } from "rl-common/models/i-characteristic-template";
import { IEntitySearchDoc } from "rl-common/models/i-entity-search-doc";
import { TrimWhitespacePipe } from "rl-common/pipes/trim-whitespace.pipe";
import { CharTypeId } from "rl-common/rl-common.consts";
import { IEntityRelationshipState } from "rl-common/services/entity/entity-relationship.models";
import { SelectState } from "rl-common/services/entity/entity.messages";
import { ParentEntityService } from "rl-common/services/entity/parent-entity/parent-entity.service";
import { IGridViewAssocEntityRec } from "rl-common/services/grid-view/models/i-grid-view-assoc-entity-rec";
import { Subscription } from "rxjs";
import { GridDataSourceBuilder } from "../../../grid/datasource/builders/grid-datasource-builder";
import { AssocEntitySelectDataSource } from "../../../grid/datasource/search/assoc-entity-select.datasource";

import { FeatureKeys, FeatureService } from "company/components/features/feature.service";
import { GridSelectType } from "rl-common/components/grid/models/grid-select-type";
import { SettingsService } from "rl-common/services/settings/settings.service";
import { map, take } from "rxjs/operators";
import { CharTypeNamePipe } from "../../../../pipes/char-type-name.pipe";
import { ComponentRelationshipComponent } from "../entity-relationship/component-relationship.component";
import { SqlComponentRelationshipComponent } from "../entity-relationship/sql-component-relationship.component";
import { OneConfigService } from "./../../../../services/one-config/one-config.service";
import { IChipDeselectedEvent } from "./../../../chip/chips/chips.component";

@Component({
	selector: "rl-component-relationships",
	templateUrl: "./component-relationships.component.html",
	styleUrls: ["./component-relationships.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [NgbAccordionDirective, NgbAccordionItem, NgbAccordionHeader, NgbAccordionToggle, NgbAccordionButton, NgClass, NgbCollapse, NgbAccordionCollapse, NgbAccordionBody, SqlComponentRelationshipComponent, ComponentRelationshipComponent, CharTypeNamePipe, TrimWhitespacePipe]
})
export class ComponentRelationshipsComponent implements OnInit, OnDestroy, AfterViewInit {
	@Input()
	charTypeId: number;

	@Input()
	templateId: number;

	@Input()
	recordId: number = null;

	@Input()
	defaultSelectStateDict: { [charTypeId: number]: SelectState<number, IGridViewAssocEntityRec> } = {};

	@Output()
	onRelationshipStateUpdated = new EventEmitter<{ [charTypeId: number]: IEntityRelationshipState<IEntitySearchDoc, unknown> }>();

	parentCharTypeId: number;
	parentRecordId: number;
	relationshipCharTypeIds: number[];
	visibleRelCharTypeIds: number[];
	charTypeTemplateDictionary: { [charTypeId: number]: ICharacteristicTemplate[] } = {};
	selectedStateDictionary: { [charTypeId: number]: IEntityRelationshipState<IEntitySearchDoc, unknown> } = {};
	dataSourceDict: { [relCharTypeId: number]: AssocEntitySelectDataSource<IEntitySearchDoc, unknown> } = {};
	requiredCharTypeDict: { [charTypeId: number]: boolean } = {};
	useSqlServerDatasources = false;
	private readonly inactiveCatalogEnabled$ = this._featureService
		.isEnabled$(FeatureKeys.InactiveCatalog)
		.pipe(take(1));

	private readonly _subscriptions: Subscription[] = [];
	chipIdFn = (chip: IEntitySearchDoc) => chip.recordID;
	chipDisplayFn = (chip: IEntitySearchDoc) => chip.title;

	get hasRels() {
		return this.visibleRelCharTypeIds && some(this.visibleRelCharTypeIds);
	}

	@ViewChild("accordion")
	accordion: NgbAccordionDirective;

	constructor(
		private readonly _oneConfigService: OneConfigService,
		private readonly _parentEntityService: ParentEntityService,
		private readonly _gridDataSourceBuilder: GridDataSourceBuilder,
		private readonly cd: ChangeDetectorRef,
		private readonly _settings: SettingsService,
		private readonly _featureService: FeatureService,
	) { }

	ngOnInit() {
		if (this._parentEntityService?.entity) {
			this.parentCharTypeId = this._parentEntityService.entity.charTypeId;
			this.parentRecordId = this._parentEntityService.entity.recordId;
			if (this._parentEntityService.entity.parent) {
				this.parentCharTypeId = this._parentEntityService.entity.parent.charTypeId;
				this.parentRecordId = this._parentEntityService.entity.parent.recordId;
			}
		}

		const possibleCharTypes = this.getPossibleCharTypes();

		this.charTypeTemplateDictionary = possibleCharTypes.reduce((acc, childCharTypeId) => {
			acc[childCharTypeId] = this._oneConfigService.getChildAssocTemplates(this.charTypeId, this.templateId, childCharTypeId);
			return acc;
		}, {});

		this.visibleRelCharTypeIds = Object.keys(this.charTypeTemplateDictionary)
			.map(key => +key)
			.filter(key => {
				const isVisible = some(this.charTypeTemplateDictionary[key]);
				if (this.charTypeId === CharTypeId.Right && this._parentEntityService.parent) {
					return isVisible && (key !== this._parentEntityService.parent.charTypeId);
				}
				return isVisible;
			});

		this.requiredCharTypeDict = this.visibleRelCharTypeIds.reduce((acc, childCharTypeId) => {
			acc[childCharTypeId] = this._oneConfigService.isChildRequired(this.charTypeId, this.templateId, childCharTypeId);
			return acc;
		}, {});

		this._settings.divSettings$.pipe(
			map(settings => settings["useSqlServerDatasource"]?.value?.toLocaleLowerCase() === "true"),
			take(1)
		).subscribe(useSql => {
			this.useSqlServerDatasources = useSql;
			this.buildDataSources();
		})
	}

	private getPossibleCharTypes() {
		if (this.charTypeId === CharTypeId.RecipientGroup) {
			return [CharTypeId.User];
		}
		if (this.charTypeId !== CharTypeId.User) {
			return [CharTypeId.User, CharTypeId.Catalog, CharTypeId.Project, CharTypeId.Inventory, CharTypeId.Mock];
		}
		return [];
	}

	ngAfterViewInit() {
		if (this.accordion && this.visibleRelCharTypeIds.indexOf(CharTypeId.Catalog) > -1) {
			this.accordion.expand(CharTypeId.Catalog.toString());
		}
	}

	buildDataSources() {
		this.dataSourceDict = this.visibleRelCharTypeIds.reduce((acc, relCharTypeId) => {
			const defaultSelectedIds = this.defaultSelectStateDict[relCharTypeId] ? this.defaultSelectStateDict[relCharTypeId].selectedIds : [];
			// TODO: div 66 hack (remove me later)
			if (this.useSqlServerDatasources && (this.isCatalogsOnRights(relCharTypeId) || this.isNestedTables(relCharTypeId) || this.isTablesOnDeal(relCharTypeId))) {
				acc[relCharTypeId] = this._gridDataSourceBuilder.sqlAssocEntitySearchDataSource(relCharTypeId,
					this.charTypeId, this.templateId, this.recordId, defaultSelectedIds, this.parentCharTypeId, this.parentRecordId);
			} else {
				acc[relCharTypeId] = this._gridDataSourceBuilder.assocEntitySearchDataSource(relCharTypeId,
					this.charTypeId, this.templateId, this.recordId, defaultSelectedIds, this.parentCharTypeId, this.parentRecordId);
			}
			if (this._parentEntityService?.parent?.parent?.charTypeId === CharTypeId.RecipientGroup) {
				acc[relCharTypeId].dataSelectStrategy.withSelectType(GridSelectType.Radio);
			}
			if (relCharTypeId === CharTypeId.Catalog &&
				(this.charTypeId === CharTypeId.Right || this.charTypeId === CharTypeId.Table)) {
				this.inactiveCatalogEnabled$.subscribe(isEnabled => {
					const strategy = acc[relCharTypeId].dataSelectStrategy;
					if (typeof (strategy as any).setInactiveCatalogFeatureEnabled === "function") {
						(strategy as any).setInactiveCatalogFeatureEnabled(isEnabled);
					}
				});
			}

			return acc;
		}, {});
	}

	getCharTypeKeys() {
		return Object.keys(this.selectedStateDictionary).filter(key => this.selectedStateDictionary[key].selectedCount > 0).map(x => +x);
	}

	isCatalogsOnRights(relCharTypeId: CharTypeId) {
		return this.charTypeId === CharTypeId.Right && relCharTypeId === CharTypeId.Catalog;
	}

	isNestedTables(relCharTypeId: CharTypeId) {
		return this.parentCharTypeId === CharTypeId.Relationship && this.charTypeId === CharTypeId.Table && relCharTypeId === CharTypeId.Catalog;
	}

	isTablesOnDeal(relCharTypeId: CharTypeId) {
		return this.parentCharTypeId === CharTypeId.Deal && this.charTypeId === CharTypeId.Table && relCharTypeId === CharTypeId.Catalog;
	}


	deselectRow(event: IChipDeselectedEvent<number, IEntitySearchDoc>, relCharTypeId: CharTypeId) {
		const dataSelectStrategy = this.dataSourceDict[relCharTypeId].dataSelectStrategy;
		if (event.isSelectAll) {
			dataSelectStrategy.deselectAll();
			return;
		}
		dataSelectStrategy.deselectRowById(event.id, event.value);
	}

	updateState(state: IEntityRelationshipState<IEntitySearchDoc, unknown>, relCharTypeId: CharTypeId) {
		this.selectedStateDictionary[relCharTypeId] = state;
		const lastState = relCharTypeId in this.selectedStateDictionary ? this.selectedStateDictionary[relCharTypeId] : null;
		const stateChanged = !isEqual(lastState, state);
		if (stateChanged) {
			this.cd.detectChanges();
		}
		this.onRelationshipStateUpdated.emit(this.selectedStateDictionary);
	}

	isRequiredInvalid(relCharTypeId: string) {
		const ct = +relCharTypeId;
		return this._oneConfigService.isChildRequired(this.charTypeId, this.templateId, ct) && this?.dataSourceDict[ct]?.formGroup.hasError("requiredGrid");
	}

	selectedRels() {
		return Object.entries(this.selectedStateDictionary)
			.map(kvp => {
				const charTypeId = +kvp[0];
				const chips = Array.from(kvp[1].selectedValues) as IEntitySearchDoc[];
				return {
					charTypeId,
					chips
				};
			})
			.filter(rel => rel.chips.length > 0);
	}

	ngOnDestroy() {
		this._subscriptions.forEach(sub => sub.unsubscribe());
	}
}
